/**
 * AC-1456: Upstream-step output_model synthesis for the introspection-driven
 * step config panels.
 *
 * Why this exists
 * ---------------
 * `FilterStepConfig`, `RDAPStepConfig`, and `ConditionBuilder` walk
 * `previousStep.agent.output_model.properties` (or
 * `previousStep.output_model.properties`) to populate field dropdowns. That
 * works fine for `run_agent` upstreams but falls flat for `queue_read` and
 * `rdap_lookup` -- those step types don't persist an `output_model`, so the
 * introspectors see no fields and the dropdowns are empty.
 *
 * The helpers here:
 *   1. `resolveUpstreamForIntrospection`: walks the React Flow edge graph to
 *      find the direct predecessor regardless of step_type, merges in the
 *      upstream node's `agent` data (the way the filter branch of
 *      StepConfigurationModal already did inline), and -- when the upstream
 *      is a `rdap_lookup` step -- synthesizes an
 *      `RDAPLookupResponse`-shaped output_model with nested_models for
 *      `RDAPDomainResult` so consumers can offer fields like
 *      `results[].domain` and `results[].status`.
 *
 *   2. `synthesizeRDAPLookupOutputModel`: the RDAP-specific synthesis,
 *      exported so future consumers (e.g. queue_write step config) can
 *      reuse it without reaching into the step graph again.
 */

import type { Edge, Node } from 'reactflow';

interface ResolveArgs {
  isEditing?: boolean;
  existingStep?: { orchestration_step_id?: string | number } | undefined;
  previousNodeId?: string | undefined;
  nodes: Node<any>[];
  edges: Edge[];
  /** Last-resort upstream the caller has already computed -- used when
   *  the edge graph lookup misses (e.g. legacy step records). */
  fallback?: any;
}

/**
 * `RDAPDomainResult`'s shape, mirrored from `backend/schemas/rdap.py`. Kept
 * inline rather than imported because (a) the introspectors only need a
 * properties bag, not a Pydantic-style class, and (b) the AC-1450
 * rich-dict normalizer accepts flat strings as type annotations, which is
 * the simplest representation. If `RDAPDomainResult`'s field set ever
 * changes, update both this constant and `frontend/src/types/rdap.ts`.
 */
const RDAP_DOMAIN_RESULT_PROPERTIES: Record<string, string> = {
  domain: 'str',
  status: 'str',
  reason: 'str',
  rdap_server: 'str',
  http_status: 'int',
  checked_at: 'str',
};

/**
 * Synthesize an `RDAPLookupResponse`-shaped output_model for a step whose
 * step_type is `rdap_lookup`. The returned object slots in where consumers
 * read `previousStep.output_model.properties`.
 *
 * The shape mirrors what `RDAPStepExecutor._build_rdap_output` (AC-1456)
 * persists: `data: { results: list[RDAPDomainResult] }` plus aggregate
 * counts. The introspectors descend `results: list[RDAPDomainResult]` via
 * the `nested_models` lookup, surfacing the per-domain fields a downstream
 * filter would want to condition on.
 */
export function synthesizeRDAPLookupOutputModel(): {
  class_name: string;
  properties: Record<string, string>;
  nested_models: Record<string, { properties: Record<string, string> }>;
} {
  return {
    class_name: 'RDAPLookupResponse',
    properties: {
      // Surface `results` at the top level as well as inside `data` so
      // ConditionBuilder/Filter can pick `results[].<field>` paths even
      // though the wire payload nests it under `data.results`. Both
      // layouts are walkable; the consumer dropdown deduplicates by leaf.
      results: 'list[RDAPDomainResult]',
      total_checked: 'int',
      available_count: 'int',
      taken_count: 'int',
      unknown_count: 'int',
    },
    nested_models: {
      RDAPDomainResult: { properties: RDAP_DOMAIN_RESULT_PROPERTIES },
    },
  };
}

/**
 * Synthesize an output_model for a filter step. The filter executor's wire
 * envelope is ``{ data: {<output_field>: filtered_list}, action, kept_count }``
 * but ``_maybe_unwrap_upstream`` in the RDAP executor strips the ``data``
 * wrapper before path resolution, so the post-unwrap shape that downstream
 * introspection sees is just ``{<output_field>: list[<element_class>]}``.
 *
 * The filter step persists ``config.output_field`` and ``config.element_class_name``
 * (saved by FilterStepConfig when the user picks the source field). We use
 * those to construct the post-unwrap properties bag so downstream RDAP /
 * conditional / filter introspection can descend into the element class
 * fields the same way it would after a run_agent upstream.
 *
 * Returns ``null`` when the filter config doesn't carry the saved class name
 * (legacy rows or partial saves). Callers leave ``output_model`` unset in
 * that case rather than guess.
 */
export function synthesizeFilterOutputModel(filterStep: any): {
  class_name: string;
  properties: Record<string, string>;
} | null {
  const cfg = filterStep?.config ?? {};
  const elementClassName: string | undefined = cfg.element_class_name;
  if (!elementClassName) return null;
  const outputField: string =
    cfg.output_field || cfg.source_field || 'items';
  return {
    class_name: 'FilterOutput',
    properties: {
      [outputField]: `list[${elementClassName}]`,
    },
  };
}

/**
 * Walk the React Flow edge graph to find the upstream step regardless of
 * step_type, merge the node's `agent` if present, and synthesize the
 * output_model when the upstream is one of the step types that doesn't
 * persist its own (`rdap_lookup`, `filter`). queue_read synthesis is still
 * done inside `RDAPStepConfig` via `useQueue` because it needs the queue's
 * `data_model_id` resolved at runtime.
 *
 * Returns `undefined` when no upstream can be found and no fallback is
 * supplied -- callers handle that by rendering empty-state copy.
 */
export function resolveUpstreamForIntrospection(args: ResolveArgs): any {
  const { isEditing, existingStep, previousNodeId, nodes, edges, fallback } = args;

  let upstreamNode: Node<any> | undefined;
  if (isEditing && existingStep?.orchestration_step_id !== undefined) {
    const incoming = edges.find(
      (e) => e.target === String(existingStep.orchestration_step_id),
    );
    upstreamNode = incoming
      ? nodes.find((n) => n.id === incoming.source)
      : undefined;
  } else if (!isEditing && previousNodeId) {
    upstreamNode = nodes.find((n) => n.id === previousNodeId);
  }

  const step: any = upstreamNode?.data?.step;
  if (!step) {
    return fallback;
  }

  const upstreamAgent = upstreamNode?.data?.agent;
  let merged: any = upstreamAgent ? { ...step, agent: upstreamAgent } : step;

  // AC-1456: rdap_lookup steps don't persist an output_model. Synthesize so
  // a downstream filter / conditional / chained rdap_lookup can introspect
  // RDAPDomainResult fields the same way it would after a run_agent step.
  if (merged.step_type === 'rdap_lookup' && !merged.output_model) {
    merged = { ...merged, output_model: synthesizeRDAPLookupOutputModel() };
  }

  // AC-1457: filter steps don't persist an output_model either. The filter's
  // saved config carries `element_class_name` + `output_field`, which is
  // enough to project the post-unwrap shape downstream introspection needs.
  if (merged.step_type === 'filter' && !merged.output_model) {
    const synthesized = synthesizeFilterOutputModel(merged);
    if (synthesized) merged = { ...merged, output_model: synthesized };
  }

  return merged;
}
